import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Box, Button, Typography, Container } from '@mui/material';
import {
  addOrEditSuggestedSearch,
  deleteSuggesteedSearch,
  fetchAvailableOption,
  fetchSuggestedSearches,
} from "../../actions/suggestedSearchAction";
import SuggestedSearchSortableItem from "./SuggestedSearchSortableItem";
import Swal from "sweetalert2";

const SuggestedSearches = () => {
  const dispatch = useDispatch();
  const suggested_search_options = useSelector(state => state.search.suggested_search_options);
  const suggested_search_data = useSelector(state => state.search.suggested_search_data);

  const [suggestedSearchData, setSuggestedSearchData] = useState([]);
  const [edited, setEdited] = useState(false);

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor)
  );

  useEffect(() => {
    dispatch(fetchAvailableOption());
    dispatch(fetchSuggestedSearches());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(suggested_search_data)) {
      setSuggestedSearchData(suggested_search_data);
    } else {
      setSuggestedSearchData([]);
    }
  }, [suggested_search_data]);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = suggestedSearchData.findIndex((item) => item.id === active.id);
      const newIndex = suggestedSearchData.findIndex((item) => item.id === over.id);
      setSuggestedSearchData((prev) => arrayMove(prev, oldIndex, newIndex));
      setEdited(true);
    }
  }, [suggestedSearchData]);

  const validateData = useCallback(() => {
    for (let i = 0; i < suggestedSearchData.length; i++) {
      const data = suggestedSearchData[i];
      if (!data.object_id || !data.object_type || !data.search_behavior) {
        throw new Error("Please fill all missing data for the suggested search.");
      }
    }
  }, [suggestedSearchData]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    validateData();
    const dataWithSortOrder = suggestedSearchData.map((item, index) => ({
      ...item,
      sort_order: index,
    }));
    dispatch(addOrEditSuggestedSearch(JSON.stringify(dataWithSortOrder)));
    setEdited(false);
  }, [dispatch, suggestedSearchData, validateData]);

  const handleDelete = useCallback((suggested_search_id, index) => {
    if (suggested_search_id) {
      dispatch(deleteSuggesteedSearch(suggested_search_id));
    }
    setSuggestedSearchData(prev => prev.filter((_, idx) => idx !== index));
  }, [dispatch]);

  const handleChange = useCallback((e, index) => {
    const value = e.value;
    const prop = e.prop;
    setSuggestedSearchData(prev => {
      const items = [...prev];
      const item = { ...items[index] };
      if (prop === "object_type" && item[prop] !== value) {
        item["object_id"] = "";
      }
      item[prop] = value;
      items[index] = item;
      return items;
    });
    setEdited(true);
  }, []);

  const addNewSuggestedSearch = useCallback(() => {
    const hasUnsavedItem = suggestedSearchData.some((item) => item.id === undefined);

    if (!hasUnsavedItem) {
      setSuggestedSearchData(prev => [...prev, { object_id: "", object_type: "", search_behavior: "" }]);
    } else {
      Swal.fire({ icon: "error", text: "Please save the current new item before adding another." });
    }
  }, [suggestedSearchData]);

  const renderSuggestedSearch = useCallback(() => {
    if (!suggested_search_options || !Array.isArray(suggestedSearchData)) {
      return <Typography variant="body1">Loading...</Typography>;
    }

    if (suggestedSearchData.length === 0) {
      return <Typography variant="body1">No searches available. Add a new one!</Typography>;
    }

    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={suggestedSearchData} strategy={verticalListSortingStrategy}>
          {suggestedSearchData.map((value, index) => (
            <SuggestedSearchSortableItem
              key={`suggestedsearch-${value.id}`}
              data={value}
              data_options={suggested_search_options}
              el_index={index}
              handleChange={handleChange}
              handleDelete={handleDelete}
            />
          ))}
        </SortableContext>
      </DndContext>
    );
  }, [suggestedSearchData, suggested_search_options, sensors, handleDragEnd, handleChange, handleDelete]);

  return (
    <Box>
      <Box my={2}>
        <Box>{renderSuggestedSearch()}</Box>

        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Button variant="outlined" color="primary" onClick={addNewSuggestedSearch}>
            Add New
          </Button>
        </Box>

        <Box display="flex" justifyContent="flex-end" mt={4}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            sx={{
              width: {
                xs: '100%',  // Full width on mobile
                sm: '50%',   // Half width on tablet
                md: '35%'    // 35% width on desktop
              }
            }}
            disabled={!edited}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default SuggestedSearches;
